import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { isDarkWidgetColor } from '../WidgetConfig';

/**
 * Deposit breakdown + disclaimer — kept in sync with InformationWidgetUser
 * (guest-booking form deposit block).
 */
const ReservationDepositSection = ({
  deposit,
  deposit_charge,
  restaurantName,
  guest,
  currentColor,
  IS_SHIRO,
  IS_HARDROCK,
  IS_TAPAYANKI,
  sx = {},
}) => {
  const theme = useTheme();
  const isDark =
    isDarkWidgetColor(currentColor) || theme.palette.mode === 'dark';
  const borderColor = isDark ? theme.palette.divider : '#d7d7d7';
  const textColor = isDark ? theme.palette.text.primary : '#111827';
  const mutedColor = isDark ? theme.palette.text.secondary : '#4B5563';

  if (!(Number(deposit) > 0)) {
    return null;
  }

  const parsedDeposit = parseFloat(deposit);
  const parsedCharge = parseFloat(deposit_charge) || 0;

  const rowSx = {
    fontSize: '12px',
    fontWeight: 500,
    color: textColor,
  };

  return (
    <Box
      sx={{
        borderRadius: '8px',
        border: '1px solid',
        borderColor,
        overflow: 'hidden',
        bgcolor: isDark ? theme.palette.background.paper : 'transparent',
        ...sx,
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor }}>
        <Typography
          sx={{
            fontWeight: 600,
            color: isDark ? theme.palette.primary.main : 'primary.main',
          }}
        >
          {parseInt(deposit, 10) === 3000
            ? 'Booking Fee (non-refundable)'
            : 'Reservation Deposit'}
        </Typography>
      </Box>

      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ ...rowSx, textTransform: 'uppercase' }}>
            {`${restaurantName} * ${guest}`}
          </Typography>
          <Typography sx={rowSx}>₦{parsedDeposit.toLocaleString()}</Typography>
        </Box>
      </Box>

      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={rowSx}>Taxes & Fees</Typography>
          <Typography sx={rowSx}>
            ₦{parsedCharge ? parsedCharge.toLocaleString() : 0}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
          }}
        >
          <Typography sx={rowSx}>Total</Typography>
          <Typography sx={rowSx}>
            ₦{' '}
            {parsedCharge
              ? (parsedDeposit + parsedCharge)?.toLocaleString()
              : parsedDeposit?.toLocaleString()}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2,
          bgcolor: isDark
            ? 'rgba(255, 255, 255, 0.04)'
            : `${currentColor.primary}0d`,
          borderRadius: '0px 0px 8px 8px',
        }}
      >
        <Typography
          sx={{
            textDecoration: 'underline',
            fontSize: '12px',
            fontWeight: 600,
            color: textColor,
          }}
        >
          Disclaimer
        </Typography>
        <Typography sx={{ fontSize: '10px', color: mutedColor }}>
          {IS_SHIRO || IS_HARDROCK ? (
            <>
              {IS_TAPAYANKI ? (
                <p>
                  Please Note: Teppanyaki is a shared dining experience. Groups
                  of fewer than 8 guests may be seated with other diners. By
                  continuing, you acknowledge and accept this seating
                  arrangement. <br />
                  <br />
                  To Cancel Or Reschedule Contact Us at
                  {IS_SHIRO && (
                    <a
                      href="tel:+234 818 629 8888"
                      style={{
                        textDecoration: 'underline',
                        fontWeight: 500,
                        color: textColor,
                      }}
                    >
                      +234 818 629 8888
                    </a>
                  )}{' '}
                  {IS_HARDROCK && (
                    <a
                      href="tel:+234 908 198 8888"
                      style={{
                        textDecoration: 'underline',
                        fontWeight: 500,
                        color: textColor,
                      }}
                    >
                      +234 908 198 8888
                    </a>
                  )}{' '}
                  To Modify Or Cancel Your Reservation.{' '}
                </p>
              ) : (
                <p>
                  We Understand That Plans May Change. If You Need to Cancel,
                  Please Take Note of Our Cancellation terms: •⁠ ⁠Cancellation
                  Within 7 Days: No Cancellation Fee Applies. •⁠ ⁠Cancellation
                  Within 2-6 Days: 10% Cancellation Fee Applies. •⁠
                  ⁠⁠Cancellation Within 24 Hours: 25% Cancellation Fee Applies.
                  •⁠ ⁠⁠No-Shows: Full Payment Is Non-Refundable.
                  <br />
                  <br /> To Cancel Or Reschedule Contact Us at{' '}
                  {IS_SHIRO && (
                    <a
                      href="tel:+234 818 629 8888"
                      style={{
                        textDecoration: 'underline',
                        fontWeight: 500,
                        color: textColor,
                      }}
                    >
                      +234 818 629 8888
                    </a>
                  )}{' '}
                  {IS_HARDROCK && (
                    <a
                      href="tel:+234 908 198 8888"
                      style={{
                        textDecoration: 'underline',
                        fontWeight: 500,
                        color: textColor,
                      }}
                    >
                      +234 908 198 8888
                    </a>
                  )}{' '}
                  To Modify Or Cancel Your Reservation.{' '}
                </p>
              )}
            </>
          ) : (
            <p>
              This deposit will be deducted from your final bill after the
              reservation has been concluded. In the event you need to cancel
              your reservation, please ensure you do so at least 12 hours before
              the reservation time, and the deposit will be refunded to your
              bank account. You will receive a reservation reminder 13 hours
              before your reservation time.
            </p>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default ReservationDepositSection;
